import React, { FC, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as StarRating } from 'assets/svgs/rating-star.svg';
import { EmptyCell } from 'components/atoms/empty-cell';
import { Option } from 'components/atoms/select';
import { RatingField as RatingFieldT } from 'components/organisms/tasks/fields';
import { ProductiveHeading07 } from 'components/styles';
import { BaseSelect } from './base-select';

interface FieldProps {
  field: RatingFieldT;
  onChange?: (selected: number | null, field: RatingFieldT) => void;
}

const Wrapper = styled.div`
  margin-top: auto;
  margin-bottom: auto;
`;

const Value = styled.span`
  ${ProductiveHeading07};
`;

export const RatingField: FC<FieldProps> = ({ field, onChange }) => {
  const { value = '' } = field;

  const [state, setState] = useState<Option | null>({
    value,
    label: value.toString(),
    active: true,
  });

  const commit = useCallback(
    (newlySelected: Option | null) => {
      setState(newlySelected);
      if (onChange) {
        onChange(newlySelected ? Number(newlySelected.value) : null, field);
      }
    },
    [field, onChange],
  );

  const options = useMemo(
    () =>
      Array.from({ length: Number(field.metaData?.ratingScale) }).map(
        (_, index) => {
          return {
            label: index.toString(),
            value: index,
            active: true,
          };
        },
      ),
    [field.metaData?.ratingScale],
  );

  const SingleValue = useCallback(
    () => (
      <Wrapper>
        <StarRating />{' '}
        <Value data-cy={`${field.fieldLabel}-rating-value`}>
          {state?.value}
        </Value>{' '}
        /{field.metaData?.ratingScale}
      </Wrapper>
    ),
    [field.fieldLabel, field.metaData?.ratingScale, state?.value],
  );

  const components = useMemo(
    () => ({
      SingleValue,
      ClearIndicator: undefined,
      DropdownIndicator: undefined,
    }),
    [SingleValue],
  );

  return value ? (
    <BaseSelect
      disabled
      options={options}
      value={state}
      onChange={commit}
      data-cy={`right.content.rating.${field.fieldLabel}`}
      components={components}
      clearable={false}
    />
  ) : (
    <EmptyCell />
  );
};
